import type { Action, ThunkDispatch } from "@reduxjs/toolkit";
import type { ReactElement, ReactNode } from "react";

import type { State } from "metabase/redux/store";
import type { ColorName } from "metabase/ui/colors/types";
import type {
  DataPermission,
  DatabaseEntityId,
  Dataset,
  Group,
  GroupId,
  GroupsPermissions,
  IconName,
  PermissionEntityId,
  PermissionSubject,
  PermissionsDatabase,
  SpecialGroupType,
  User,
} from "metabase-types/api";
import { DataPermissionValue } from "metabase-types/api";

import { definePluginSlot } from "../slot";
import type { PluginGroupManagersType } from "../types";

// These describe the entries EE plugins contribute to the admin permissions
// editor. They live here (not in metabase/admin) because shared plugin code
// cannot import from feature modules; metabase/admin/permissions/types
// re-exports them.
export interface PermissionOption {
  label: string;
  value: DataPermissionValue;
  icon: IconName;
  iconColor: ColorName;
}

export interface PermissionAction {
  label: string;
  icon: IconName;
  iconColor: ColorName;
  onSelect: (
    entityId: PermissionEntityId | undefined,
    groupId: GroupId,
    view: "database" | "group",
  ) => void;
}

export interface PermissionConfirmationProps {
  title: string;
  message: ReactNode;
  confirmButtonText: string;
  cancelButtonText: string;
}

export type PostActionFunction = (
  entityId: PermissionEntityId,
  groupId: GroupId,
  view: "database" | "group",
  value: DataPermissionValue,
  getState: () => State,
) =>
  | Action
  | ((
      dispatch: ThunkDispatch<State, unknown, Action>,
      getState: () => State,
    ) => unknown)
  | void;

const getDefaultAdminPermissionsDatabaseRoutes = (): ReactElement[] => [];
const getDefaultAdminPermissionsDatabaseGroupRoutes = (): ReactElement[] => [];
const getDefaultAdminPermissionsDatabasePostActions = (): {
  impersonated: PostActionFunction | null;
} => ({
  impersonated: null,
});
const getDefaultAdminPermissionsDatabaseActions = (): {
  impersonated: PermissionAction[];
} => ({
  impersonated: [],
});
const getDefaultAdminPermissionsTableOptions = (): PermissionOption[] => [];
const getDefaultAdminPermissionsTableRoutes = (): ReactElement[] => [];
const getDefaultAdminPermissionsTableGroupRoutes = (): ReactElement[] => [];
const getDefaultAdminPermissionsTableFieldsOptions =
  (): PermissionOption[] => [];
const getDefaultAdminPermissionsTableFieldsConfirmations = (): Array<
  (
    _permissions: GroupsPermissions,
    _groupId: number,
    _entityId: PermissionEntityId,
    _value: DataPermissionValue,
  ) => PermissionConfirmationProps | undefined
> => [];
const getDefaultAdminPermissionsTableFieldsActions = (): {
  sandboxed: PermissionAction[];
} => ({
  sandboxed: [],
});
const getDefaultAdminPermissionsTableFieldsPostAction = (): {
  sandboxed: PostActionFunction | null;
} => ({
  sandboxed: null,
});

export const PLUGIN_ADMIN_PERMISSIONS_DATABASE_ROUTES = definePluginSlot(
  getDefaultAdminPermissionsDatabaseRoutes,
);
export const PLUGIN_ADMIN_PERMISSIONS_DATABASE_GROUP_ROUTES = definePluginSlot(
  getDefaultAdminPermissionsDatabaseGroupRoutes,
);
export const PLUGIN_ADMIN_PERMISSIONS_DATABASE_POST_ACTIONS = definePluginSlot(
  getDefaultAdminPermissionsDatabasePostActions,
);
export const PLUGIN_ADMIN_PERMISSIONS_DATABASE_ACTIONS = definePluginSlot(
  getDefaultAdminPermissionsDatabaseActions,
);
export const PLUGIN_ADMIN_PERMISSIONS_TABLE_OPTIONS = definePluginSlot(
  getDefaultAdminPermissionsTableOptions,
);
export const PLUGIN_ADMIN_PERMISSIONS_TABLE_ROUTES = definePluginSlot(
  getDefaultAdminPermissionsTableRoutes,
);
export const PLUGIN_ADMIN_PERMISSIONS_TABLE_GROUP_ROUTES = definePluginSlot(
  getDefaultAdminPermissionsTableGroupRoutes,
);
export const PLUGIN_ADMIN_PERMISSIONS_TABLE_FIELDS_OPTIONS = definePluginSlot(
  getDefaultAdminPermissionsTableFieldsOptions,
);
export const PLUGIN_ADMIN_PERMISSIONS_TABLE_FIELDS_CONFIRMATIONS =
  definePluginSlot(getDefaultAdminPermissionsTableFieldsConfirmations);
export const PLUGIN_ADMIN_PERMISSIONS_TABLE_FIELDS_ACTIONS = definePluginSlot(
  getDefaultAdminPermissionsTableFieldsActions,
);
export const PLUGIN_ADMIN_PERMISSIONS_TABLE_FIELDS_POST_ACTION =
  definePluginSlot(getDefaultAdminPermissionsTableFieldsPostAction);

const getDefaultDataPermissions = () => ({
  permissionsPayloadExtraSelectors: [],
  hasChanges: [],
  upgradeViewPermissionsIfNeeded: null,
  shouldRestrictNativeQueryPermissions: () => false,
});

export const PLUGIN_DATA_PERMISSIONS: {
  permissionsPayloadExtraSelectors: ((
    state: State,
  ) => [Record<string, undefined | { group_id: number }[]>, string[]])[];
  hasChanges: ((state: State) => boolean)[];
  shouldRestrictNativeQueryPermissions: (
    permissions: GroupsPermissions,
    groupId: number,
    entityId: PermissionEntityId,
    permission: DataPermission,
    value: DataPermissionValue,
    database: PermissionsDatabase,
  ) => boolean;

  upgradeViewPermissionsIfNeeded:
    | ((
        permissions: GroupsPermissions,
        groupId: number,
        entityId: PermissionEntityId,
        value: DataPermissionValue,
        database: PermissionsDatabase,
        permission: DataPermission,
      ) => GroupsPermissions)
    | null;
} = definePluginSlot(getDefaultDataPermissions);

const getDefaultAdminUserMenuItems = (): Array<
  (user: User) => React.ReactNode
> => [];
const getDefaultAdminUserMenuRoutes = (): (() => React.ReactNode)[] => [];

export const PLUGIN_ADMIN_USER_MENU_ITEMS = definePluginSlot(
  getDefaultAdminUserMenuItems,
);
export const PLUGIN_ADMIN_USER_MENU_ROUTES = definePluginSlot(
  getDefaultAdminUserMenuRoutes,
);

const getDefaultAdvancedPermissions = () => ({
  addDatabasePermissionOptions: (
    permissions: PermissionOption[],
    _database: PermissionsDatabase,
  ) => permissions,
  addSchemaPermissionOptions: (
    permissions: PermissionOption[],
    _value: string,
  ) => permissions,
  addTablePermissionOptions: (
    permissions: PermissionOption[],
    _value: string,
  ) => permissions,
  getDatabaseLimitedAccessPermission: (
    _value: string,
  ): DataPermissionValue | null => null,
  isAccessPermissionDisabled: (
    _value: string,
    _subject: "schemas" | "tables" | "fields",
  ) => false,
  isRestrictivePermission: (_value: string) => false,
  shouldShowViewDataColumn: false,
  defaultViewDataPermission: DataPermissionValue.UNRESTRICTED,
});

export const PLUGIN_ADVANCED_PERMISSIONS = definePluginSlot(
  getDefaultAdvancedPermissions,
);

const getDefaultFeatureLevelPermissions = () => ({
  getFeatureLevelDataPermissions: ({
    entityId: _entityId,
    groupId: _groupId,
    groupType: _groupType,
    permissions: _permissions,
    dataAccessPermissionValue: _dataAccessPermissionValue,
    defaultGroup: _defaultGroup,
    permissionSubject: _permissionSubject,
    permissionView: _permissionView,
    showTransformPermissions: _showTransformPermissions,
  }: {
    entityId: DatabaseEntityId;
    groupId: number;
    groupType: SpecialGroupType;
    permissions: GroupsPermissions;
    dataAccessPermissionValue: DataPermissionValue;
    defaultGroup: Group;
    permissionSubject: PermissionSubject;
    permissionView?: "group" | "database";
    showTransformPermissions?: boolean;
  }) => {
    // Unjustified type cast. FIXME
    return [] as any;
  },
  getDataColumns: ({
    subject: _subject,
    groupType: _groupType,
    isExternal: _isExternal,
    showTransformPermissions: _showTransformPermissions,
  }: {
    subject: PermissionSubject;
    groupType?: SpecialGroupType;
    isExternal?: boolean;
    showTransformPermissions?: boolean;
    // Unjustified type cast. FIXME
  }) => [] as any,
  getDownloadWidgetMessageOverride: (_result: Dataset): string | null => null,
  canDownloadResults: (_result: Dataset): boolean => true,
  // Unjustified type cast. FIXME
  dataModelQueryProps: {} as any,
  // Unjustified type cast. FIXME
  databaseDetailsQueryProps: {} as any,
});

export const PLUGIN_FEATURE_LEVEL_PERMISSIONS = definePluginSlot(
  getDefaultFeatureLevelPermissions,
);

const getDefaultAdminPermissionsTabs = () => ({
  getRoutes: (): ReactNode => null,
  // Unjustified type cast. FIXME
  tabs: [] as { name: string; value: string }[],
});

export const PLUGIN_ADMIN_PERMISSIONS_TABS = definePluginSlot(
  getDefaultAdminPermissionsTabs,
);

const getDefaultApplicationPermissions = () => ({
  getRoutes: (): ReactNode => null,
  // Unjustified type cast. FIXME
  tabs: [] as any,
});

export const PLUGIN_APPLICATION_PERMISSIONS = definePluginSlot(
  getDefaultApplicationPermissions,
);

const getDefaultGroupManagers = (): PluginGroupManagersType => ({
  // Unjustified type cast. FIXME
  UserTypeToggle: () => null as any,
  UserTypeCell: null,

  getChangeMembershipConfirmation: () => null,
  getRemoveMembershipConfirmation: () => null,

  deleteGroup: null,
  confirmDeleteMembershipAction: null,
  confirmUpdateMembershipAction: null,
});

export const PLUGIN_GROUP_MANAGERS = definePluginSlot(getDefaultGroupManagers);
